import React, { useCallback, useEffect, useState } from 'react'
import useParams from 'core/hooks/useParams'
import ModalForm from 'core/elements/modal/ModalForm'
import { updateNetwork, updateSubnet } from './actions'
import useUpdateAction from 'core/hooks/useUpdateAction'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import AdminStatePicklist from './AdminStatePicklist'
import { maxValueValidator, minValueValidator } from 'core/utils/fieldValidators'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import PicklistField from 'core/components/validatedForm/DropdownField'
import ZonePicklist from './dns/records/ZonePicklist'
import { DNS_ZONE_INFO, MAKE_SHARED_INFO } from './constants'
import { updateSubnetsDnsPublishFlag } from './helpers'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'

export default function EditPhysicalNetworkModal({ rows: [network], onClose }) {
  const [loaded, setLoaded] = useState(false)
  const defaultParams = {}
  const isAdminUser = isAdmin()

  // Detect Simple Networks (L2-only)
  const isSimpleNetwork = network?.isSimpleNetwork

  const { params, getParamsUpdater, updateParams, setParams } = useParams<{
    name?: string
    description?: string
    adminState?: string
    shared?: boolean
    enablePortSecurity?: boolean
    mtu?: number
    external?: boolean
    dns_domain?: string
  }>(defaultParams)

  const { update, updating, error, reset } = useUpdateAction(updateNetwork)
  const { update: updateSubnetFn, updating: updatingSubnet, error: errorSubnet } = useUpdateAction(
    updateSubnet,
  )

  useEffect(() => {
    // Prevent resetting of form from background reloads
    if (loaded) {
      return
    }
    updateParams({
      name: network.name,
      description: network.description,
      adminState: network.admin_state_up ? 'Up' : 'Down',
      shared: network.shared,
      enablePortSecurity: network.port_security_enabled,
      mtu: network.mtu,
      external: network['router:external'],
      dns_domain: network.dns_domain,
    })
    setLoaded(true)
  }, [network, loaded])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    onClose()
  }

  const submitForm = useCallback(async () => {
    const primaryFields = {
      name: params.name,
      description: params.description,
      admin_state_up: params.adminState !== 'Down',
      dns_domain: params.dns_domain,
    }
    const baseBody = {
      network: {
        ...primaryFields,
        shared: params.shared,
        port_security_enabled: params.enablePortSecurity,
        ['router:external']: params.external,
      },
    }
    const body = !isAdminUser
      ? { network: { ...primaryFields } }
      : network?.type === 'physical'
      ? { network: { ...baseBody.network, mtu: params.mtu } }
      : baseBody
    const { success } = await update({
      id: network.id,
      body,
    })
    if (!success) return

    const shouldClearSubnetDNSPublishFixedIp =
      params?.dns_domain === '' &&
      network?.dns_domain !== '' &&
      Array.isArray(network?.subnetDetails) &&
      network.subnetDetails.length > 0

    if (shouldClearSubnetDNSPublishFixedIp) {
      await updateSubnetsDnsPublishFlag(network.subnetDetails, updateSubnetFn)
    }

    handleClose()
  }, [network, params, handleClose, isAdminUser])

  return (
    <ModalForm
      open
      title={`Edit Network`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingSubnet}
      error={error || errorSubnet}
      submitTitle={`Update Network`}
      loading={!loaded}
    >
      <ServiceUnhealthyInfo action="Network Edit" network />
      <FormFieldSection title="Network Configuration">
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          required
        />
        <TextField
          id="description"
          label="Description"
          onChange={getParamsUpdater('description')}
          value={params.description}
        />
        <AdminStatePicklist value={params.adminState} onChange={getParamsUpdater('adminState')} />
        {/* Hide DNS Zone for Simple Networks (L2-only) */}
        {!isSimpleNetwork && (
          <PicklistField
            DropdownComponent={ZonePicklist}
            id="zoneId"
            label="DNS Zone"
            onChange={getParamsUpdater('dns_domain')}
            value={params.dns_domain}
            info={DNS_ZONE_INFO}
            setLabelAsValue={true}
          />
        )}
        {!isAdminUser ? null : (
          <>
            {network?.type === 'physical' && (
              <TextField
                id="mtu"
                label="MTU"
                onChange={(value) => getParamsUpdater('mtu')(value as number)}
                validations={[minValueValidator(1280), maxValueValidator(9000)]}
                value={params.mtu}
                type="number"
                info="Maximum Transmission Unit (MTU) for the network. MTU must be between 1280 and 9000"
              />
            )}
            {/* External Network - Available for both Managed and Simple Networks */}
            {!isSimpleNetwork && (
              <CheckboxField
                id="external"
                label="Allow use of Public IPs (External Network)"
                value={params.external}
                onChange={getParamsUpdater('external')}
                info="Set checked if the network should be able to assign public IPs (external network)"
              />
            )}
            {/* Port Security - Available for both Managed and Simple Networks */}
            {!isSimpleNetwork && (
              <CheckboxField
                id="enablePortSecurity"
                label="Enable Port Security"
                value={params.enablePortSecurity}
                onChange={getParamsUpdater('enablePortSecurity')}
                info="If checked, newly created ports on this network will have port security enabled by default."
              />
            )}
            <CheckboxField
              id="shared"
              label="Make Shared (Across All Tenants)"
              value={params.shared}
              onChange={getParamsUpdater('shared')}
              info={MAKE_SHARED_INFO}
            />
          </>
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
